import React, { useRef, useLayoutEffect, useState, useEffect, useContext } from "react";
import { WrapParentContext } from "./WrapParentContext.tsx";
import "./Frame.scss";

// LAYOUT * the most important one! *
// - direction: horizontal | vertical | wrap
// - width: number | fill | hug
// - height: number | fill | hug
// - align: top-left | top-center | top-right | center-left | center | center-right | bottom-left | bottom-center | bottom-right

// - min width, max width: number
// - min height, max height: number 
// - Lock aspect ratio: boolean
// - Horizontal padding -> left padding, right padding. DEFAULT: 0px
// - Vertical padding -> top padding, bottom padding. DEFAULT: 0px

// FILL
// - type: solid | gradient | pattern | image

// STROKE


// EFFECTS

enum Directions {
   VERTICAL = "vertical",
   HORIZONTAL = "horizontal",
   WRAP = "wrap"
}

enum Positions {
   TOP_LEFT = "top-left",
   TOP_CENTER = "top-center",
   TOP_RIGHT = "top-right",
   CENTER_LEFT = "center-left",
   POS_CENTER = "center",
   CENTER_RIGHT = "center-right",
   BOTTOM_LEFT = "bottom-left",
   BOTTOM_CENTER = "bottom-center",
   BOTTOM_RIGHT = "bottom-right"
}
const { TOP_LEFT, TOP_CENTER, TOP_RIGHT, CENTER_LEFT, POS_CENTER, CENTER_RIGHT, BOTTOM_LEFT, BOTTOM_CENTER, BOTTOM_RIGHT } = Positions;

enum Alignments {
   START = "start",
   CENTER = "center",
   END = "end"
}
const { START, CENTER, END } = Alignments;

interface Layout {
   // auto layout
   direction?: Directions;
   align: "top-left" | "top-center" | "top-right" | "center-left" | "center" | "center-right" | "bottom-left" | "bottom-center" | "bottom-right";
   gap?: "auto" | number | number[];
   ignore?: boolean;
   order?: number;

   // frame dimensions
   width?: number | string;
   minWidth?: number;
   maxWidth?: number;
   horizontalPadding?: number | number[];

   height?: number | string;
   minHeight?: number;
   maxHeight?: number;
   verticalPadding?: number | number[];

   // aspect ratio
   aspectRatio?: string;
}

interface Frame {
   layout?: Layout;
   children?: React.ReactNode;
   className?: string;
}

const defaultLayout = {
   direction: "horizontal",
   align: "top-left",
   gap: "auto",
   ignore: false,
   order: 0,
   width: "hug",
   minWidth: 0,
   maxWidth: null,
   horizontalPadding: 0,
   height: "hug",
   minHeight: 0,
   maxHeight: null,
   verticalPadding: 0,
   aspectRatio: null
}

const defaultFill = {
   type: "solid",
   input: null
};

const Frame: React.FC<Frame> = ({ layout = defaultLayout, children, ...props }) => {
   const {
      direction = "horizontal",
      align = "top-left",
      gap = "auto",
      ignore = false,
      order = 0,
      width = "hug",
      minWidth = 0,
      maxWidth = null,
      horizontalPadding = 0,
      height = "hug",
      minHeight = 0,
      maxHeight = null,
      verticalPadding = 0,
      aspectRatio = null
   } = layout;

   const { className } = props;
   const childRef = useRef(null);
   const parentContext = useContext(WrapParentContext);
   const parentDirection = useRef(parentContext && parentContext.direction ? parentContext.direction : null).current;

   const _getLayout = () => {
      const _getIgnore = () => {
         if (ignore)
            return { position: "absolute" };
      }

      const _getGapAndAlignment = () => {
         const POSITIONS = [ // for row main axis [main axis, cross axis]
            [TOP_LEFT, CENTER_LEFT, BOTTOM_LEFT],
            [TOP_CENTER, POS_CENTER, BOTTOM_CENTER],
            [TOP_RIGHT, CENTER_RIGHT, BOTTOM_RIGHT]
         ];

         const ALIGNMENT_TYPES = [START, CENTER, END];
         let mainAxisAlign = "space-between", crossAxisAlign = START;

         for (let x = 0; x < POSITIONS.length; x++) {
            const y = POSITIONS[x].indexOf(align);

            if (y !== -1) {
               // determine cross axis alignment
               if (direction === Directions.HORIZONTAL || direction === Directions.WRAP) {
                  crossAxisAlign = ALIGNMENT_TYPES[y];

                  if (Number.isInteger(gap))
                     mainAxisAlign = ALIGNMENT_TYPES[x];
               } else if (direction === Directions.VERTICAL) {
                  crossAxisAlign = ALIGNMENT_TYPES[x];

                  if (Number.isInteger(gap))
                     mainAxisAlign = ALIGNMENT_TYPES[y];
               }

               break;
            }
         }

         return {
            justifyContent: mainAxisAlign,
            alignItems: crossAxisAlign,
            gap: Number.isInteger(gap) ? `${gap}px` : null
         };
      }

      const _getDimension = () => {
         // Fixed aspect ratio, summary:
         // - both directions can be fixed
         // - only one direction at most can be fill
         // - priority: fixed, fill, hug
         // - if both fill, prioritize the direction that matches the parent direction

         // Valid scenarios for fixed aspect ratio
         // [1] fixed, fixed -> no a.r.
         // [2] fixed, hug -> no action
         // [3] fixed, fill -> remove fill
         // [4] fill, fill -> remove a fill
         // [5] fill, hug -> no action
         // [6] hug, hug -> no action
         // ** If parent is not a frame, width fill takes precedence **

         const _getWidth = () => {
            // parent is a frame
            if (parentDirection && !ignore) {
               // width is fill
               if (width === "fill") {
                  // With aspect ratio:
                  if (aspectRatio !== null) {
                     // MAIN AXIS
                     if (parentDirection === "horizontal") {
                        if (height === "fill" || height === "hug")
                           return { overflow: "hidden", flex: 1 };
                     }

                     else if (parentDirection === "wrap") {
                        if (height === "fill" || height === "hug")
                           return { overflow: "hidden" }
                     }

                     // CROSS AXIS
                     else if (parentDirection === "vertical") {
                        if (height === "fill" || Number.isInteger(height))
                           return { width: "fit-content" };

                        else if (height === "hug")
                           return { alignSelf: "stretch" };
                     }
                  }

                  // Without aspect ratio
                  else {
                     // MAIN AXIS
                     if (parentDirection === "horizontal")
                        return { flex: 1 };

                     // 🟠
                     else if (parentDirection === "wrap")
                        return { flex: 1 };

                     // CROSS AXIS
                     else if (parentDirection === "vertical")
                        return { width: "100%" }
                  }
               }

               // width is fixed
               else if (Number.isInteger(width)) {
                  // width is fixed, height is fill -> height becomes hug
                  // width is fixed, height is fixed -> as usual
                  // width is fixed, height is hug -> as usual

                  // MAIN AXIS
                  if (parentDirection === "horizontal" || parentDirection === "wrap")
                     return { flex: `0 0 ${width}px`, overflow: "hidden" };

                  // CROSS AXIS
                  else if (parentDirection === "vertical")
                     return { width: `${width}px`, overflow: "hidden" };
               }

               // width is hug 
               else if (width === "hug") {
                  // MAIN AXIS
                  if (parentDirection === "horizontal" || parentDirection === "wrap")
                     return { flex: "0 0 auto" };

                  // CROSS AXIS
                  return { width: "fit-content" };
               }
            }
            // parent is not a frame, no flex required
            else { // parent is not a frame
               // width is fill
               if (width === "fill") {
                  // aspect ratio locked:
                  // - height is also fill -> make width fill (PRECEDENCE)
                  // - OR height is hug -> make width fill
                  if (aspectRatio === null || height === "fill" || height === "hug")
                     return { width: "100%" };
               }

               // width is fixed
               else if (Number.isInteger(width)) {
                  // width is fixed, height is fill -> height becomes hug
                  // width is fixed, height is fixed -> as usual
                  // width is fixed, height is hug -> as usual
                  return { width: `${width}px` };
               }

               // width is hug
               else if (width === "hug")
                  return { width: "fit-content" };
            }
         }

         const _getHeight = () => {
            // parent is a frame
            if (parentDirection) {
               // height is fill
               if (height === "fill" && !ignore) {
                  // With aspect ratio:
                  if (aspectRatio !== null) {
                     // MAIN AXIS
                     if (parentDirection === "vertical") {
                        if (width === "fill")
                           return { overflow: "hidden", flex: 1 };
                        else if (Number.isInteger(width))
                           return { flex: "0 0 auto" };
                     }

                     // CROSS AXIS
                     else if (parentDirection === "horizontal" || parentDirection === "wrap")
                        return { height: "fit-content" };
                  }

                  // Without aspect ratio
                  else {
                     // MAIN AXIS 
                     if (parentDirection === "vertical")
                        return { flex: 1 };

                     // CROSS AXIS
                     else if (parentDirection === "horizontal")
                        return { alignSelf: "stretch" };

                     else if (parentDirection === "wrap") {
                        return { alignSelf: "stretch" };
                     }

                  }
               }

               // height is fixed
               else if (!isNaN(height)) {
                  // height is fixed, width is fill -> width becomes hug
                  // height is fixed, width is fixed -> as usual
                  // height is fixed, width is hug -> as usual

                  // CROSS AXIS
                  if (parentDirection === "horizontal" || parentDirection === "wrap") {
                     if (!ignore)
                        return { height: `${height}px`, overflow: "hidden" };
                  }

                  // MAIN AXIS
                  else if (parentDirection === "vertical") {
                     if (ignore)
                        return { height: `${height}px` };

                     return { flex: `0 0 ${height}px`, overflow: "hidden" };
                  }
               }

               // height is hug (default)
               else {
                  // MAIN AXIS
                  if (parentDirection === "vertical") {
                     if (ignore)
                        return { flex: "0 0 auto" };

                     return { flex: "0 0 auto" };
                  }

                  // CROSS AXIS
                  else if (parentDirection === "horizontal") {
                     if (ignore)
                        return { height: "fit-content" };

                     return { height: "fit-content" };
                  }

               }
            }

            // parent is not a frame, no flex required 
            else {
               // height is fill
               if (height === "fill") {
                  // width is also fill -> make width fill (PRECEDENCE)
                  // OR width is hug -> make height fill
                  if (width === "hug")
                     return { height: "100%" };
               }

               // height is fixed
               if (Number.isInteger(height)) {
                  // height is fixed, width is fill -> width becomes hug
                  // height is fixed, width is fixed -> as usual
                  // height is fixed, width is hug -> as usual

                  return { height: `${height}px` };
               }

               // height is hug
               else if (height === "hug")
                  return { height: "fit-content" };
            }
         }

         const _getMinMax = () => {
            return {
               minWidth: minWidth ? minWidth : 0,
               maxWidth: maxWidth ? maxWidth : "initial",
               minHeight: minHeight ? minHeight : 0,
               maxHeight: maxHeight ? maxHeight : "initial",
            };
         }

         return {
            ..._getWidth(),
            ..._getHeight(),
            ..._getMinMax(),
         }
      }

      const _getPadding = () => {
         let allPadding = [0, 0, 0, 0]; // top right bottom left

         // horizontal padding
         if (Number.isInteger(horizontalPadding)) {
            allPadding[1] = horizontalPadding;
            allPadding[3] = horizontalPadding;
         }
         else if (Array.isArray(horizontalPadding)) {
            allPadding[1] = horizontalPadding[0];
            allPadding[3] = horizontalPadding[1];
         }

         // vertical padding
         if (Number.isInteger(verticalPadding)) {
            allPadding[0] = verticalPadding;
            allPadding[2] = verticalPadding;
         }
         else if (Array.isArray(verticalPadding)) {
            allPadding[0] = verticalPadding[0];
            allPadding[2] = verticalPadding[1];
         }

         return {
            padding: `${allPadding[0]}px ${allPadding[1]}px ${allPadding[2]}px ${allPadding[3]}px`
         };
      }

      const _getAspectRatio = () => {
         if (!Number.isInteger(height) || !Number.isInteger(width))
            return { aspectRatio: aspectRatio };
      }

      const _getOrder = () => {
         return { order };
      }

      return {
         ..._getIgnore(),
         ..._getGapAndAlignment(),
         ..._getDimension(),
         ..._getPadding(),
         ..._getAspectRatio(),
         ..._getOrder(),
      }
   }

   const [style, setStyle] = useState({ ..._getLayout() });

   const setAlignContentStretch = () => {
      // target behaviour for edge case:
      // - if child component (B) with height: fill has a parent component (A) with direction: wrap, set itself (B) to have align-self: stretch, and set parent (A) to have align-content: stretch

      setStyle(prevStyle => {
         if (prevStyle && prevStyle.alignContent !== "stretch")
            return {
               ...prevStyle,
               alignContent: "stretch"
            };

         return prevStyle;
      });
   }

   // post-mount modifications
   useEffect(() => {
      if (parentContext)
         if (height === "fill" && parentDirection === "wrap") {
            parentContext.setAlignContentStretch();
         }
   }, [style]);

   useEffect(() => {
      setStyle(() => ({ ..._getLayout() }));
   }, [layout]);

   return (
      <div
         {...props}
         className={`frame frame-${direction} ${align} ${className}`}
         style={style}
         ref={childRef}
      >
         <WrapParentContext.Provider value={{ direction: direction, style: style, setAlignContentStretch }}>
            {children}
         </WrapParentContext.Provider>
      </div >
   )

}

export default Frame;